import { ReactNode, useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

type ResizableBoxProps = {
  children: ReactNode;
};

export default function ResizableBox({ children }: ResizableBoxProps) {
  const design = useSelector((state: any) => state?.design);
  const [resizeActive, setResizeActive] = useState(false);

  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (!design.enableChatWindowResize) {
      return;
    }

    const startX = e.clientX;
    const startY = e.clientY;

    const parentWindow = window.top;
    const maxWidth = parentWindow?.innerWidth;
    const maxHeight = parentWindow?.innerHeight;

    let lastExecutionTime = 0;
    const throttleDelay = 16;

    const handleMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastExecutionTime < throttleDelay) return;

      lastExecutionTime = now;

      const chatWindow = window.parent.document.getElementById('BotPenguin-messenger');
      if (!chatWindow) {
        console.error('Chat window not found!');
        return;
      }

      const computedStyle = window.getComputedStyle(chatWindow);
      const startWidth = parseFloat(computedStyle.width);
      const startHeight = parseFloat(computedStyle.height);

      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      let newWidth = startWidth - dx;
      let newHeight = startHeight - dy;

      if (maxWidth && maxHeight) {
        newWidth = Math.max(100, Math.min(newWidth, (maxWidth - 20)));
        newHeight = Math.max(100, Math.min(newHeight, (maxHeight - 20)));
      }

      localStorage.setItem('userPreferredWidth', String(newWidth));
      localStorage.setItem('userPreferredHeight', String(newHeight));
      chatWindow.style.setProperty('transition', 'width 0s ease, height 0s ease');
      chatWindow.style.setProperty('width', `${newWidth}px`, 'important');
      chatWindow.style.setProperty('height', `${newHeight}px`, 'important');
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setResizeActive(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    setResizeActive(true);
  }, [design.enableChatWindowResize]);

  useEffect(() => {
    const handleMouseDownWrapper = (e: MouseEvent) => handleMouseDown(e);


    return () => {
      document.removeEventListener('mousemove', handleMouseDownWrapper);
      document.removeEventListener('mouseup', handleMouseDownWrapper);
    };
  }, [resizeActive, handleMouseDown]);

  return (
    <div id='resizable-box' className='relative'>
      {design.enableChatWindowResize && (
        <>
          <div
            className='absolute z-40 top-0 left-0 h-full w-2.5 cursor-ew-resize bg-transparent'
            onMouseDown={(e: any) => handleMouseDown(e)}
          />
          <div
            className='absolute z-40 top-0 left-0 w-full h-2.5 cursor-n-resize bg-transparent'
            onMouseDown={(e: any) => handleMouseDown(e)}
          />
        </>
      )}
      {children}
    </div>
  );
}